/**
 * AnnouncementModalProvider
 * 
 * Provides announcement context to the app, managing the announcement queue,
 * view tracking, and modal display based on priority and frequency limits.
 * 
 * Requirements: 2.2, 2.6
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from '@/context/auth-context';
import { AnnouncementModal } from '@/components/announcements/AnnouncementModal';
import { announcementsAPI } from '@/lib/api';
import type { Announcement, PollResults, LocalViewCounts } from '@/lib/announcement-types';

const VIEW_COUNTS_KEY = 'carevault_announcement_views';

interface AnnouncementContextType {
  /** Current announcement being displayed */
  currentAnnouncement: Announcement | null;
  /** All active announcements in queue */
  announcements: Announcement[];
  /** Whether announcements are loading */
  isLoading: boolean;
  /** Dismiss the current announcement */
  dismissCurrent: () => void;
  /** Refetch announcements */
  refetch: () => Promise<void>;
  /** Check if there are more announcements */
  hasMoreAnnouncements: boolean;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

/**
 * Get local view counts from localStorage
 */
function getLocalViewCounts(): LocalViewCounts {
  try {
    const stored = localStorage.getItem(VIEW_COUNTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Increment local view count for an announcement
 */
function incrementLocalViewCount(announcementId: string): void {
  try {
    const counts = getLocalViewCounts();
    counts[announcementId] = (counts[announcementId] || 0) + 1;
    localStorage.setItem(VIEW_COUNTS_KEY, JSON.stringify(counts));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Check if announcement should be shown based on local view count and frequency
 */
function shouldShowAnnouncement(announcement: Announcement): boolean {
  const counts = getLocalViewCounts();
  const viewCount = counts[announcement.id] || 0;
  const frequency = announcement.display_frequency;

  if (frequency === 'once' && viewCount >= 1) {
    return false;
  }

  if (frequency === 'until_dismissed') {
    return true;
  }

  // Numeric frequency (show N times)
  const numericFrequency = parseInt(frequency, 10);
  if (!isNaN(numericFrequency) && viewCount >= numericFrequency) {
    return false;
  }

  return true;
}

interface AnnouncementModalProviderProps {
  children: ReactNode;
}

export function AnnouncementModalProvider({ children }: AnnouncementModalProviderProps) {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pollResults, setPollResults] = useState<PollResults | null>(null);
  const [hasSubmittedPoll, setHasSubmittedPoll] = useState(false);
  const [hasRecordedView, setHasRecordedView] = useState(false);

  const currentAnnouncement = announcements[currentIndex] || null;
  const hasMoreAnnouncements = currentIndex < announcements.length - 1;

  /**
   * Fetch active announcements from the API
   */
  const fetchAnnouncements = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await announcementsAPI.getActiveForUser();
      const fetchedAnnouncements: Announcement[] = response.data?.announcements || [];

      // Filter based on local view counts (double-check client-side)
      const filteredAnnouncements = fetchedAnnouncements.filter(shouldShowAnnouncement);

      setAnnouncements(filteredAnnouncements);
      setCurrentIndex(0);
      setPollResults(null);
      setHasSubmittedPoll(false);
      setHasRecordedView(false);

      // Open modal if there are announcements
      if (filteredAnnouncements.length > 0) {
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Fetch announcements when user logs in
   */
  useEffect(() => {
    if (user?.emailConfirmed) {
      // Small delay to let the dashboard load first
      const timer = setTimeout(() => {
        fetchAnnouncements();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.emailConfirmed, fetchAnnouncements]);

  /**
   * Record view when current announcement changes
   */
  useEffect(() => {
    if (currentAnnouncement && isModalOpen && !hasRecordedView) {
      // Record view on server
      announcementsAPI.recordView(currentAnnouncement.id, false).catch((err) => {
        console.warn('Failed to record announcement view:', err);
      });

      // Increment local view count
      incrementLocalViewCount(currentAnnouncement.id);
      setHasRecordedView(true);
    }
  }, [currentAnnouncement?.id, isModalOpen, hasRecordedView]);

  /**
   * Move to next announcement or close modal
   */
  const moveToNext = useCallback(() => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setPollResults(null);
      setHasSubmittedPoll(false);
      setHasRecordedView(false);
    } else {
      setIsModalOpen(false);
    }
  }, [currentIndex, announcements.length]);

  /**
   * Dismiss the current announcement
   */
  const dismissCurrent = useCallback(async () => {
    if (!currentAnnouncement) return;

    try {
      // Record dismissal on server
      await announcementsAPI.recordView(currentAnnouncement.id, true);
    } catch (err) {
      console.warn('Failed to record dismissal:', err);
    }

    moveToNext();
  }, [currentAnnouncement, moveToNext]);

  /**
   * Handle close button click
   */
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * Handle button click
   */
  const handleButtonClick = useCallback(async (buttonId: string, url: string) => {
    if (!currentAnnouncement) return;

    try {
      await announcementsAPI.recordEngagement(currentAnnouncement.id, 'button_click', {
        button_id: buttonId,
        url,
      });
    } catch (err) {
      console.warn('Failed to record button click:', err);
    }

    // Open URL in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [currentAnnouncement]);

  /**
   * Handle poll submission
   */
  const handlePollSubmit = useCallback(async (optionId: string): Promise<PollResults | null> => {
    if (!currentAnnouncement) return null;

    try {
      const response = await announcementsAPI.submitPollResponse(currentAnnouncement.id, optionId);
      const results = response.data?.pollResults || null;
      setPollResults(results);
      setHasSubmittedPoll(true);
      return results;
    } catch (err: unknown) {
      // Handle already responded case
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 409) {
          try {
            const resultsResponse = await announcementsAPI.getPollResults(currentAnnouncement.id);
            const results = resultsResponse.data?.pollResults || null;
            setPollResults(results);
            setHasSubmittedPoll(true);
            return results;
          } catch {
            // Ignore fetch error
          }
        }
      }
      console.error('Failed to submit poll response:', err);
      throw err;
    }
  }, [currentAnnouncement]);

  /**
   * Refetch announcements
   */
  const refetch = useCallback(async () => {
    await fetchAnnouncements();
  }, [fetchAnnouncements]);

  const contextValue: AnnouncementContextType = {
    currentAnnouncement,
    announcements,
    isLoading,
    dismissCurrent,
    refetch,
    hasMoreAnnouncements,
  };

  return (
    <AnnouncementContext.Provider value={contextValue}>
      {children}
      
      {/* Render modal when there's a current announcement */}
      {isModalOpen && currentAnnouncement && (
        <AnnouncementModal
          announcement={currentAnnouncement}
          onClose={handleClose}
          onDismiss={dismissCurrent}
          onButtonClick={handleButtonClick}
          onPollSubmit={handlePollSubmit}
          pollResults={pollResults}
          hasSubmittedPoll={hasSubmittedPoll}
        />
      )}
    </AnnouncementContext.Provider>
  );
}

/**
 * Hook to access announcement context
 */
export function useAnnouncementContext() {
  const context = useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error('useAnnouncementContext must be used within an AnnouncementModalProvider');
  }
  return context;
}

export default AnnouncementModalProvider;
